import { useCallback, useMemo, useRef, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AsyncButton } from "../ui/async-button";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { Input } from "../ui/input";
import {
  computeCoverGeometry,
  estimateCoverImageDpi,
  parseCoverLayout,
  wrapPixelsAtDpi,
  type BookCoverLayout,
  type CoverPlatform,
  type PaperStock,
} from "../../../shared/coverGeometry";
import type { TrimSize } from "../../../shared/publishFormattingShared";

type Props = {
  bookTitle: string;
  authorName: string;
  pageCount: number;
  trimSize: TrimSize;
  platform: CoverPlatform;
  layout: BookCoverLayout;
  onChange: (layout: BookCoverLayout) => void;
  onExport?: () => void;
  exporting?: boolean;
};

function OverlayRect({
  rect,
  color,
  label,
  scale,
}: {
  rect: { x: number; y: number; w: number; h: number };
  color: string;
  label?: string;
  scale: number;
}) {
  return (
    <div
      title={label}
      className="pointer-events-none absolute box-border border"
      style={{
        left: rect.x * scale,
        top: rect.y * scale,
        width: Math.max(1, rect.w * scale),
        height: Math.max(1, rect.h * scale),
        borderColor: color,
      }}
    />
  );
}

export function CoverEditor({
  bookTitle,
  authorName,
  pageCount,
  trimSize,
  platform,
  layout,
  onChange,
  onExport,
  exporting,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const [imgNatural, setImgNatural] = useState<{ w: number; h: number } | null>(null);
  const [showOverlays, setShowOverlays] = useState(true);

  const effectiveLayout = useMemo(
    () =>
      parseCoverLayout({
        ...layout,
        platform,
        trimSize,
        pageCount,
      }),
    [layout, platform, trimSize, pageCount]
  );

  const geometry = useMemo(
    () =>
      computeCoverGeometry({
        platform: effectiveLayout.platform,
        paperStock: effectiveLayout.paperStock,
        trimSize: effectiveLayout.trimSize,
        pageCount: effectiveLayout.pageCount,
      }),
    [effectiveLayout]
  );

  const previewScale = Math.min(520 / geometry.wrapWIn, 360 / geometry.wrapHIn);
  const px300 = wrapPixelsAtDpi(geometry, 300);

  const dpi = useMemo(() => {
    if (!imgNatural) return null;
    return estimateCoverImageDpi({
      imageWidthPx: imgNatural.w,
      imageHeightPx: imgNatural.h,
      geometry,
      scale: effectiveLayout.image.scale,
    });
  }, [imgNatural, geometry, effectiveLayout.image.scale]);

  const patch = useCallback(
    (partial: Partial<BookCoverLayout>) => {
      onChange(
        parseCoverLayout({
          ...effectiveLayout,
          ...partial,
          image: { ...effectiveLayout.image, ...(partial.image ?? {}) },
          updatedAt: new Date().toISOString(),
        })
      );
    },
    [effectiveLayout, onChange]
  );

  const onFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === "string" ? reader.result : null;
      if (!src) return;
      const img = new Image();
      img.onload = () => {
        setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
        patch({
          image: {
            src,
            offsetXIn: 0,
            offsetYIn: 0,
            scale: 1,
          },
        });
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const wrapAspect = geometry.wrapWIn / geometry.wrapHIn;
  const imgAspect = imgNatural ? imgNatural.w / imgNatural.h : wrapAspect;
  let drawW = geometry.wrapWIn * effectiveLayout.image.scale;
  let drawH = drawW / imgAspect;
  if (imgAspect > wrapAspect) {
    drawH = geometry.wrapHIn * effectiveLayout.image.scale;
    drawW = drawH * imgAspect;
  }
  const imgLeft =
    (geometry.wrapWIn - drawW) / 2 + effectiveLayout.image.offsetXIn;
  const imgTop =
    (geometry.wrapHIn - drawH) / 2 + effectiveLayout.image.offsetYIn;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-[0.82rem] text-foreground">Paper</label>
        <Select
          value={effectiveLayout.paperStock}
          onValueChange={(value) => patch({ paperStock: value as PaperStock })}
        >
          <SelectTrigger className="min-w-35" size="sm" aria-label="Paper">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cream">Cream (B&amp;W)</SelectItem>
            <SelectItem value="white">White (B&amp;W)</SelectItem>
            <SelectItem value="standard-color">Standard color</SelectItem>
            <SelectItem value="premium-color">Premium color</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
          Import image
        </Button>
        <Input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <label className="flex cursor-pointer select-none items-center gap-1.5 text-[0.82rem] text-foreground" htmlFor="cover-guides">
          <Checkbox
            id="cover-guides"
            checked={showOverlays}
            onCheckedChange={(v) => setShowOverlays(v === true)}
          />
          Guides
        </label>
        {onExport ? (
          <AsyncButton
            type="button"
            className="font-semibold"
            busy={exporting}
            busyLabel="Exporting…"
            onClick={onExport}
          >
            Export wrap PDF
          </AsyncButton>
        ) : null}
      </div>

      <p className="text-xs leading-snug text-muted-foreground">
        Wrap {geometry.wrapWIn.toFixed(4)}&quot; × {geometry.wrapHIn.toFixed(4)}&quot; · spine{" "}
        {geometry.spineWIn.toFixed(4)}&quot; ({pageCount} pp × caliper) · 300 DPI canvas {px300.w}×{px300.h}px
        {dpi != null ? (
          <>
            {" "}
            · image ~{Math.round(dpi)} DPI
            {dpi < 300 ? <span className="text-amber-700 dark:text-amber-300"> (below 300 — may look soft in print)</span> : null}
          </>
        ) : null}
        {!geometry.allowSpineText ? (
          <>
            {" "}
            · spine text disabled (need ≥{geometry.spineTextMinPages} pages)
          </>
        ) : null}
      </p>

      <div
        className="relative mx-auto overflow-hidden bg-muted shadow-md select-none"
        style={{
          width: geometry.wrapWIn * previewScale,
          height: geometry.wrapHIn * previewScale,
          cursor: effectiveLayout.image.src ? "grab" : "default",
        }}
        onPointerDown={(e) => {
          if (!effectiveLayout.image.src) return;
          (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
          dragRef.current = {
            x: e.clientX,
            y: e.clientY,
            ox: effectiveLayout.image.offsetXIn,
            oy: effectiveLayout.image.offsetYIn,
          };
        }}
        onPointerMove={(e) => {
          if (!dragRef.current) return;
          const dx = (e.clientX - dragRef.current.x) / previewScale;
          const dy = (e.clientY - dragRef.current.y) / previewScale;
          patch({
            image: {
              ...effectiveLayout.image,
              offsetXIn: dragRef.current.ox + dx,
              offsetYIn: dragRef.current.oy + dy,
            },
          });
        }}
        onPointerUp={() => {
          dragRef.current = null;
        }}
        onWheel={(e) => {
          if (!effectiveLayout.image.src) return;
          e.preventDefault();
          const next = Math.min(4, Math.max(0.5, effectiveLayout.image.scale * (e.deltaY < 0 ? 1.05 : 0.95)));
          patch({ image: { ...effectiveLayout.image, scale: next } });
        }}
      >
        {effectiveLayout.image.src ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={effectiveLayout.image.src}
            alt=""
            draggable={false}
            className="pointer-events-none absolute"
            style={{
              left: imgLeft * previewScale,
              top: imgTop * previewScale,
              width: drawW * previewScale,
              height: drawH * previewScale,
            }}
          />
        ) : (
          <EmptyState
            compact
            title="Import a cover image"
            description="Then pan or scroll to zoom."
            className="absolute inset-0 items-center justify-center rounded-none border-0 bg-transparent text-center"
          />
        )}

        {showOverlays ? (
          <>
            <OverlayRect rect={geometry.regions.backTrim} color="rgba(0,0,0,0.45)" label="Back trim" scale={previewScale} />
            <OverlayRect rect={geometry.regions.spine} color="rgba(124,58,237,0.9)" label="Spine" scale={previewScale} />
            <OverlayRect rect={geometry.regions.frontTrim} color="rgba(0,0,0,0.45)" label="Front trim" scale={previewScale} />
            <OverlayRect rect={geometry.regions.backSafe} color="rgba(34,211,238,0.85)" label="Back safe" scale={previewScale} />
            <OverlayRect rect={geometry.regions.frontSafe} color="rgba(34,211,238,0.85)" label="Front safe" scale={previewScale} />
            <OverlayRect rect={geometry.regions.spineSafe} color="rgba(244,114,182,0.9)" label="Spine safe" scale={previewScale} />
            <OverlayRect rect={geometry.regions.barcode} color="rgba(234,179,8,0.95)" label="Barcode clear" scale={previewScale} />
            <div
              className="pointer-events-none absolute inset-0 shadow-[inset_0_0_0_1px_rgba(236,72,153,0.7)]"
              title="Bleed edge"
            />
          </>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Spine title">
          <Input
            value={effectiveLayout.spineTitle || bookTitle}
            disabled={!geometry.allowSpineText}
            onChange={(e) => patch({ spineTitle: e.target.value })}
          />
        </Field>
        <Field label="Spine author">
          <Input
            value={effectiveLayout.spineAuthor || authorName}
            disabled={!geometry.allowSpineText}
            onChange={(e) => patch({ spineAuthor: e.target.value })}
          />
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            patch({ image: { ...effectiveLayout.image, scale: 1, offsetXIn: 0, offsetYIn: 0 } })
          }
        >
          Fit / reset
        </Button>
        <span className="text-xs text-muted-foreground">Zoom {effectiveLayout.image.scale.toFixed(2)}×</span>
      </div>
    </div>
  );
}
